import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Award, Heart, MapPin, Users, Flag, ShieldCheck, Instagram, Facebook, Linkedin, Quote, Star } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import useReveal from "../lib/useReveal";
import useSEO from "../lib/useSEO";
import { SOCIALS } from "../lib/socials";

const testimonials = [
  {
    quote: "Weekends were never our problem, we wanted to bring more guests in during slower periods. Rae Enterprise built geofences around nearby casinos, hotels, and entertainment venues, then retargeted those visitors with our weekday promotions based on player tier. It was the first campaign we ran that was designed around how our guests actually move and make plans, from real data.",
    name: "Michael T",
    business: "Casino General Manager",
  },
  {
    quote: "I had already spent money on social media ads, but they reached too many people who were not shopping for a vehicle. Taylor introduced us to competitor conquest targeting: when someone visited another dealership, we could stay in front of that shopper afterward. The strategy was direct, practical, and made sense for our sales process.",
    name: "Dmitri V",
    business: "Auto Shop Owner",
  },
  {
    quote: "In political campaigns where targeting can be limited, we were able to use strategies our opponents weren't even aware of. Rae Enterprise translated our client's priority voter universe into an addressable programmatic campaign, managed multiple messages, and provided reporting our team could actually use. They gave our client a sophisticated digital capability without creating more work for the campaign staff.",
    name: "Ethan W",
    business: "Political Consultant",
  },
  {
    quote: "I didn't want our med spa to become another business constantly advertising discounts. Taylor helped us create a more thoughtful approach, reaching audiences interested in injectables, laser treatments, and skincare while using website retargeting to keep us top of mind. The campaign felt polished, educational, and consistent with the experience we provide in person.",
    name: "Olivia B",
    business: "Med Spa Owner",
  },
];

export default function AboutPage() {
  useReveal();
  useSEO({
    title: "About — Built in Seattle | Founder Taylor",
    description: "Meet Taylor, founder and CEO of Rae Enterprise LLC. A Seattle-based luxury marketing firm with a 7-person, fully US-based team delivering compliant precision geofencing.",
    path: "/about",
  });
  return (
    <SiteShell>
      <section className="pt-32 pb-20 border-b border-white/5" data-testid="about-hero">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-end">
          <div>
            <div className="kicker mb-6">About</div>
            <h1 className="h-display text-white text-[48px] sm:text-[72px] lg:text-[96px] leading-[0.9]">
              BUILT IN<br />
              <span className="text-[#d4af37]">SEATTLE.</span>
            </h1>
          </div>
          <p className="text-white/70 max-w-[520px] text-[16px] leading-relaxed">
            Rae Enterprise LLC is a luxury marketing firm founded on a single
            principle — businesses thrive when they are seen by the right people.
            We engineer that visibility with precision, intelligence and elegance.
          </p>
        </div>
      </section>

      {/* Founder */}
      <section className="py-24" data-testid="about-founder">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] bg-[#111111] border border-[#d4af37]/15 relative overflow-hidden">
              <img
                src="/founder.jpg"
                alt="Taylor — Founder & CEO of Rae Enterprise LLC"
                className="w-full h-full object-cover object-top"
                data-testid="founder-photo"
              />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="kicker mb-5">Founder &amp; CEO</div>
            <h2 className="h-display text-white text-[36px] sm:text-[48px] mb-8">
              MEET TAYLOR<span className="text-[#d4af37]">.</span>
            </h2>
            <div className="space-y-5 text-white/75 text-[15px] leading-relaxed">
              <p>
                "My name is Taylor — founder and CEO of Rae Enterprise LLC. I started
                this company with one conviction: influence shouldn't be reserved for
                brands with massive budgets. Small and mid-size businesses deserve the
                same precision, the same data, and the same presence as the giants
                they compete with."
              </p>
              <p>
                "What began as a Seattle marketing startup built on community and
                connection has grown into a specialized geofencing and programmatic
                firm. Every campaign we build answers one question — how do we put
                your brand in front of the right person at the exact moment it
                matters?"
              </p>
              <p>
                "Rae Enterprise is, and always will be, the tool I use to help
                businesses grow and thrive — through the pursuit of excellence, with
                a team that enjoys the work and clients who enjoy the results."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team stats */}
      <section className="py-20 bg-[#0d0d0d] border-y border-[#d4af37]/10" data-testid="about-team-stats">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#d4af37]/10">
            <TeamStat
              icon={<Users size={24} strokeWidth={1.2} />}
              stat="7"
              label="Team Members"
              desc="A tight, senior team — strategy, data, creative and campaign ops under one roof."
              testId="stat-team-size"
            />
            <TeamStat
              icon={<Flag size={24} strokeWidth={1.2} />}
              stat="100%"
              label="US-Based"
              desc="Every member of our team lives and works within the United States. No outsourcing, ever."
              testId="stat-us-based"
            />
            <TeamStat
              icon={<ShieldCheck size={24} strokeWidth={1.2} />}
              stat="FULLY"
              label="Compliant"
              desc="Privacy-safe targeting that adheres to U.S. regulations — sensitive categories screened and reviewed on every campaign."
              testId="stat-compliant"
            />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#111111] border-b border-[#d4af37]/10" data-testid="about-values">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="text-center mb-16">
            <div className="kicker mb-4">What We Stand For</div>
            <h2 className="h-display text-white text-[36px] sm:text-[52px] lg:text-[64px]">
              OUR <span className="text-[#d4af37]">PRINCIPLES.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#d4af37]/10">
            <Value icon={<Compass size={26} strokeWidth={1.2} />} title="Precision" desc="Every fence, every audience, every creative — engineered around your goals." />
            <Value icon={<Award size={26} strokeWidth={1.2} />} title="Excellence" desc="Luxury-grade attention to detail in strategy, creative and reporting." />
            <Value icon={<Heart size={26} strokeWidth={1.2} />} title="Community" desc="We grow when our clients grow. Real partnerships, not transactions." />
            <Value icon={<MapPin size={26} strokeWidth={1.2} />} title="Local Roots" desc="Seattle-built. Working with brands across the country and beyond." />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#0d0d0d] border-b border-[#d4af37]/10" data-testid="about-testimonials">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="kicker mb-4">What Clients Say</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-14">
            IN THEIR <span className="text-[#d4af37]">WORDS.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#d4af37]/10">
            {testimonials.map((t, i) => (
              <figure key={t.name} className="bg-[#0b0b0b] p-10 lg:p-12 flex flex-col reveal" data-testid={`testimonial-card-${i}`}>
                <Quote size={26} strokeWidth={1.2} className="text-[#d4af37] mb-6" />
                <blockquote className="text-white/80 text-[15px] leading-relaxed flex-1">"{t.quote}"</blockquote>
                <figcaption className="mt-8 pt-6 border-t border-white/5">
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={12} strokeWidth={1} className="text-[#d4af37] fill-[#d4af37]" />
                    ))}
                  </div>
                  <div className="text-white text-[13px] tracking-[0.15em] uppercase">{t.name}</div>
                  <div className="text-white/50 text-[12px] mt-1">{t.business}</div>
                </figcaption>
              </figure>
            ))}
          </div>
          <a
            href="https://g.page/r/Cf1jEdpNJ_I1EBM/review"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 inline-flex items-center gap-2 kicker text-[#d4af37] hover:text-[#f2c66d]"
            data-testid="about-google-review-link"
          >
            <Star size={14} strokeWidth={1.4} /> Worked with us? Leave a Google review →
          </a>
        </div>
      </section>

      {/* Socials */}
      <section className="py-24" data-testid="about-socials">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="kicker mb-4">Follow The Work</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-12">
            CONNECT WITH <span className="text-[#d4af37]">US.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#d4af37]/10">
            <SocialCard
              icon={<Instagram size={30} strokeWidth={1.2} />}
              platform="Instagram"
              handle={SOCIALS.instagram.handle}
              url={SOCIALS.instagram.url}
              testId="about-social-instagram"
            />
            <SocialCard
              icon={<Facebook size={30} strokeWidth={1.2} />}
              platform="Facebook"
              handle={SOCIALS.facebook.handle}
              url={SOCIALS.facebook.url}
              testId="about-social-facebook"
            />
            <SocialCard
              icon={<Linkedin size={30} strokeWidth={1.2} />}
              platform="LinkedIn"
              handle={SOCIALS.linkedin.handle}
              url={SOCIALS.linkedin.url}
              testId="about-social-linkedin"
            />
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/5" data-testid="about-cta">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <h3 className="h-display text-white text-[32px] sm:text-[44px]">
            LET'S TALK ABOUT YOUR <span className="text-[#d4af37]">BRAND.</span>
          </h3>
          <Link to="/contact" className="btn-gold btn-label mt-10 inline-flex" data-testid="about-cta-btn">
            Start The Conversation <ArrowRight size={16} strokeWidth={1.6} />
          </Link>
        </div>
      </section>
    </SiteShell>
  );
}

function TeamStat({ icon, stat, label, desc, testId }) {
  return (
    <div className="bg-[#0b0b0b] p-10 lg:p-12" data-testid={testId}>
      <div className="text-[#d4af37] mb-6">{icon}</div>
      <div className="h-display text-[#d4af37] text-[52px] leading-none">{stat}</div>
      <div className="text-white text-[13px] tracking-[0.25em] uppercase mt-2 mb-4">{label}</div>
      <p className="text-white/60 text-[13px] leading-relaxed">{desc}</p>
    </div>
  );
}

function SocialCard({ icon, platform, handle, url, testId }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-[#0b0b0b] hover:bg-[#111111] p-10 lg:p-12 flex items-center gap-6 group transition-colors duration-500"
      data-testid={testId}
    >
      <span className="text-[#d4af37]">{icon}</span>
      <span>
        <span className="block text-white text-[15px] tracking-[0.2em] uppercase">{platform}</span>
        <span className="block text-white/55 text-[13px] mt-1 group-hover:text-[#d4af37] transition-colors">{handle}</span>
      </span>
      <ArrowRight size={16} strokeWidth={1.4} className="ml-auto text-white/30 group-hover:text-[#d4af37] group-hover:translate-x-1 transition" />
    </a>
  );
}

function Value({ icon, title, desc }) {
  return (
    <div className="bg-[#0b0b0b] p-10">
      <div className="text-[#d4af37] mb-5">{icon}</div>
      <h4 className="text-white text-[14px] tracking-[0.22em] uppercase mb-3">{title}</h4>
      <p className="text-white/60 text-[13px] leading-relaxed">{desc}</p>
    </div>
  );
}
